import {type ClassValue, clsx} from 'clsx';
import {twMerge} from 'tailwind-merge';
import { BillingMethod, type ServiceBundle } from '@ruta/shared';

// Re-export date utilities
export { parseDate, formatDateDisplay, deactivationDisplay, deactivatedDisplay, startDisplay, REQUESTED_START_LABEL } from './utils/date';
export { deactivationBannerDisplay, type DeactivationBannerDisplay } from './utils/deactivationBanner';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get the rate suffix based on billing method
 * @param billingMethod - The billing method of the service
 * @returns The rate suffix string ("/hr" or "/season")
 */
export function getRateSuffix(billingMethod: BillingMethod | string | undefined): string {
  switch (billingMethod) {
    case BillingMethod.PER_HOUR:
    case 'per_hour':
      return '/hr';
    case BillingMethod.PACKAGE:
    case 'package':
      return '/season';
    case BillingMethod.PER_VISIT:
    case 'per_visit':
    default:
      return '/visit';
  }
}

/** "$150", or "$150.50" when there are cents — for prices set in whole dollars. One formatter for the app and the API (`@ruta/shared`). */
export { formatWholeDollars } from '@ruta/shared';

/**
 * Format a number as currency using proper localization
 * @param amount - The amount to format
 * @param currency - The currency code (default: 'USD')
 * @param locale - The locale to use (default: 'en-US')
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US',
  maxDecimals: number = 2,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: maxDecimals,
  }).format(amount);
}

/**
 * The one-line description of how a package (ServiceBundle) is priced, used
 * wherever a package is named — relationships list, quote, profile. Fixed
 * packages show their amount; derived packages are priced per customer from
 * the member rates quoted to them, so we say that, with the discount if any.
 */
export function bundlePriceDisplay(bundle: Pick<ServiceBundle, 'pricingPolicy'>): string {
  const policy = bundle.pricingPolicy;
  if (policy.mode === 'fixed') return formatCurrency(policy.amount);
  if (policy.discount && policy.discount.value > 0) {
    return policy.discount.type === 'percentage'
      ? `${policy.discount.value}% off member rates`
      : `${formatCurrency(policy.discount.value)} off member rates`;
  }
  return 'Member rates';
}

/**
 * The bundle row's second line: "3 services · 10% off member rates". A fixed
 * price is already the row's price, so the line is just the member count.
 */
export function bundleRowSubtitle(bundle: Pick<ServiceBundle, 'pricingPolicy'>, memberCount: number): string {
  const count = `${memberCount} service${memberCount === 1 ? '' : 's'}`;
  return bundle.pricingPolicy.mode === 'fixed' ? count : `${count} · ${bundlePriceDisplay(bundle)}`;
}

/**
 * Format a number as currency without the currency symbol
 * @param amount - The amount to format
 * @param locale - The locale to use (default: 'en-US')
 * @returns Formatted number string
 */
export function formatAmount(
  amount: number,
  locale: string = 'en-US',
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a decimal number with specified decimal places
 * Ensures leading zero and trailing decimals (e.g., 0.50, 3.00)
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 2)
 * @param locale - The locale to use (default: 'en-US')
 * @returns Formatted decimal string
 */
export function formatDecimal(
  value: number | string | null | undefined,
  decimals: number = 2,
  locale: string = 'en-US',
): string {
  // Handle null/undefined/empty values
  if (value === null || value === undefined || value === '') {
    return `0.${'0'.repeat(decimals)}`;
  }

  // Convert to number if string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Handle invalid numbers
  if (isNaN(numValue)) {
    return `0.${'0'.repeat(decimals)}`;
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false, // No thousands separator for non-currency decimals
  }).format(numValue);
}

/**
 * Parse a formatted string back to a number
 * Handles currency symbols, commas, and other formatting
 * @param value - The formatted string to parse
 * @returns Numeric value or NaN if invalid
 */
export function parseFormattedNumber(value: string): number {
  if (!value) return 0;

  // Remove currency symbols, spaces, and commas
  const cleanedValue = value.replace(/[$,\s]/g, '');

  return parseFloat(cleanedValue);
}

/**
 * Format a value as it's being typed in an input field
 * Maintains decimal formatting while allowing user input
 * @param value - The current input value
 * @param decimals - Number of decimal places to maintain
 * @returns Formatted value suitable for input field
 */
export function formatInputValue(
  value: string,
  decimals: number = 2,
): string {
  // Allow empty string
  if (!value) return '';

  // Allow single decimal point
  if (value === '.') return '0.';

  // Remove non-numeric characters except decimal point
  let cleaned = value.replace(/[^\d.]/g, '');

  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }

  // Limit decimal places
  if (parts.length === 2 && parts[1].length > decimals) {
    cleaned = parts[0] + '.' + parts[1].slice(0, decimals);
  }

  // Add leading zero if starts with decimal
  if (cleaned.startsWith('.')) {
    cleaned = '0' + cleaned;
  }

  return cleaned;
}

/**
 * Debounce a function call
 * @param func - The function to debounce
 * @param wait - The delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function(...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}
